/**
 * Client device identity for the web portal (stable install id + browser label).
 * Device id is stored in localStorage; server registration runs after login.
 */

import api from './axiosInstance';
import { API } from '../config/apiConfig';

const DEVICE_ID_KEY = 'dreamey_device_id';
const DEVICE_NAME_KEY = 'dreamey_device_name';
const PLATFORM = 'web';

let registerInFlight = null;

function createDeviceUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function buildDeviceNameFromNavigator() {
  if (typeof navigator === 'undefined') return 'Unknown browser';

  const uaData = navigator.userAgentData;
  if (uaData?.platform) {
    const brands = Array.isArray(uaData.brands)
      ? uaData.brands.map((b) => b.brand).filter(Boolean).join(', ')
      : '';
    if (brands) return `${brands} on ${uaData.platform}`;
    return `Web on ${uaData.platform}`;
  }

  const ua = String(navigator.userAgent || '').trim();
  if (!ua) return 'Unknown browser';
  return ua.length > 255 ? `${ua.slice(0, 252)}...` : ua;
}

/**
 * Ensure device id and name exist in localStorage (capture once per browser install).
 */
export function getOrCreateDeviceIdentity() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  let deviceName = localStorage.getItem(DEVICE_NAME_KEY);

  if (!deviceId) {
    deviceId = createDeviceUuid();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }

  if (!deviceName) {
    deviceName = buildDeviceNameFromNavigator();
    localStorage.setItem(DEVICE_NAME_KEY, deviceName);
  }

  return { deviceId, deviceName, platform: PLATFORM };
}

/**
 * POST device to API (upsert). Requires auth token. Non-fatal; dedupes concurrent calls.
 */
export async function registerUserDevice() {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) return null;

  if (registerInFlight) return registerInFlight;

  const { deviceId, deviceName, platform } = getOrCreateDeviceIdentity();

  registerInFlight = api
    .post(API.userDevice, { deviceId, deviceName, platform })
    .then((res) => res?.data ?? null)
    .catch((err) => {
      console.warn(
        'Device registration failed:',
        err?.response?.data?.error || err?.message || err
      );
      return null;
    })
    .finally(() => {
      registerInFlight = null;
    });

  return registerInFlight;
}
